import React, { useEffect, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import colors from '../../styles/theme/foundation/colors';

/**
 * Progress bar for work whose duration is unknown. It creeps toward `ceiling` so it never looks
 * finished before the caller says so, then jumps to 100 once `isComplete` is true.
 */
const CreepingProgressBar = ({
  label,
  isComplete = false,
  ceiling = 90,
  tickMs = 100,
  initialProgress = 4
}) => {
  const [progress, setProgress] = useState(initialProgress);

  useEffect(() => {
    if (isComplete || progress >= ceiling) return undefined;

    const interval = setInterval(() => {
      setProgress(prev => Math.min(prev + 1, ceiling));
    }, tickMs);

    return () => clearInterval(interval);
  }, [progress, isComplete, ceiling, tickMs]);

  const width = isComplete ? 100 : progress;

  return (
    <Box pb={2} pt={4} px={2} textAlign="center">
      <Box
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={width}
        bg={colors.backgroundScale['100']}
        borderRadius="full"
        h="20px"
        overflow="hidden"
        role="progressbar"
        w="100%"
      >
        <Box
          bgGradient={`linear-gradient(90deg, ${colors.primaryScale['800']} 0%, ${colors.primaryScale['600']} 55%, ${colors.primaryScale['300']} 100%)`}
          borderRadius="full"
          h="100%"
          transition="width 0.6s ease-in-out"
          w={`${width}%`}
        />
      </Box>

      {label && (
        <Text color={colors.greyScale['700']} lineHeight="32px" mt={6}>
          {label}
        </Text>
      )}
    </Box>
  );
};

CreepingProgressBar.propTypes = {
  label: PropTypes.string,
  isComplete: PropTypes.bool,
  ceiling: PropTypes.number,
  tickMs: PropTypes.number,
  initialProgress: PropTypes.number
};

export default CreepingProgressBar;
